var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '@utils/i18n';
import { Button, Text } from '@ui';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
import { SelectInput } from '@ui/molecules/inputs/form/SelectInput';
import { searchBuildings } from '../../../../TenantKnowledgeBase/services/TkbService';
import { EXCLUSION_SCOPE_LEVELS } from './checkExclusionConfig';
import { ExclusionMultiSelect } from './ExclusionMultiSelect';
const NS = 'features.settings.ai_inspection_manager';
const t = (key, params = {}) => translate(key, Object.assign({ namespace: NS }, params));
/**
 * Add Exclusion modal (IP-14862). "Exclude by" single-select drives a dynamic
 * searchable multi-select of values; one Add fans out to one exclusion row per
 * selected value × the card's issue types.
 */
export function AddExclusionModal({ isOpen, onClose, checkName, accounts, accountTags, buildingTags, excludedByLevel = {}, saving = false, error, onAdd }) {
    const [scopeLevel, setScopeLevel] = useState('account');
    const [values, setValues] = useState([]);
    // If a host keeps this mounted across `isOpen` toggles (see Storybook usage)
    // rather than remounting per check, reset local picker state whenever the
    // modal (re)opens so a stale scope/value selection from a previous check
    // doesn't leak into the next one.
    useEffect(() => {
        if (isOpen) {
            setScopeLevel('account');
            setValues([]);
        }
    }, [isOpen]);
    // Sets of scope keys already excluded for this check, per axis — filtered out
    // of the pickers so an already-excluded target is never offered again.
    const excludedAccounts = useMemo(() => { var _a; return new Set((_a = excludedByLevel.account) !== null && _a !== void 0 ? _a : []); }, [excludedByLevel]);
    const excludedAccountTags = useMemo(() => { var _a; return new Set((_a = excludedByLevel.account_tag) !== null && _a !== void 0 ? _a : []); }, [excludedByLevel]);
    const excludedBuildings = useMemo(() => { var _a; return new Set((_a = excludedByLevel.building) !== null && _a !== void 0 ? _a : []); }, [excludedByLevel]);
    const excludedBuildingTags = useMemo(() => { var _a; return new Set((_a = excludedByLevel.building_tag) !== null && _a !== void 0 ? _a : []); }, [excludedByLevel]);
    const scopeOptions = useMemo(() => EXCLUSION_SCOPE_LEVELS.map((lvl) => ({
        value: lvl,
        label: t(`check_exclusions.scope_level_options.${lvl}`)
    })), []);
    const accountOptions = useMemo(() => accounts.map((a) => ({ value: String(a.value), label: a.label })).filter((o) => !excludedAccounts.has(o.value)), [accounts, excludedAccounts]);
    const accountTagOptions = useMemo(() => accountTags.filter((tag) => !excludedAccountTags.has(tag)).map((tag) => ({ value: tag, label: tag })), [accountTags, excludedAccountTags]);
    const buildingTagOptions = useMemo(() => buildingTags.filter((tag) => !excludedBuildingTags.has(tag)).map((tag) => ({ value: tag, label: tag })), [buildingTags, excludedBuildingTags]);
    const resetAndClose = () => {
        setScopeLevel('account');
        setValues([]);
        onClose();
    };
    const handleScopeChange = (lvl) => {
        setScopeLevel(lvl);
        setValues([]);
    };
    const handleAdd = () => {
        if (values.length === 0)
            return;
        onAdd(scopeLevel, values);
    };
    const asyncBuildingSearch = (query) => __awaiter(this, void 0, void 0, function* () {
        const results = yield searchBuildings(query);
        return results
            .map((b) => ({ value: String(b.value), label: b.label }))
            .filter((o) => !excludedBuildings.has(o.value));
    });
    const valueFieldLabel = t(`check_exclusions.value_field_label.${scopeLevel}`);
    const valuePlaceholder = t(`check_exclusions.value_placeholder.${scopeLevel}`);
    const valueSearchPlaceholder = t(`check_exclusions.value_search_placeholder.${scopeLevel}`);
    const titleId = 'aim-add-exclusion-title';
    return (React.createElement(Modal, { isOpen: isOpen, onClose: resetAndClose, ariaLabelledBy: titleId },
        React.createElement(ModalHeader, { titleId: titleId, title: t('check_exclusions.add_title', { check: checkName }) }),
        React.createElement(ModalBody, null,
            error && (React.createElement(Text, { as: "p", className: "aim-exclusion-modal__error" }, error)),
            React.createElement(Text, { as: "p", variant: "body", className: "aim-exclusion-modal__intro" }, t('check_exclusions.intro')),
            React.createElement("div", { className: "aim-exclusion-modal__field" },
                React.createElement(SelectInput, { id: "aim-exclusion-scope", label: t('check_exclusions.exclude_by'), options: scopeOptions, value: scopeLevel, onChange: handleScopeChange })),
            React.createElement("div", { className: "aim-exclusion-modal__field" },
                scopeLevel === 'account' && (React.createElement(ExclusionMultiSelect, { label: valueFieldLabel, placeholder: valuePlaceholder, searchPlaceholder: valueSearchPlaceholder, emptyMessage: t('check_exclusions.no_options'), options: accountOptions, selected: values, onChange: setValues })),
                scopeLevel === 'account_tag' && (React.createElement(ExclusionMultiSelect, { label: valueFieldLabel, placeholder: valuePlaceholder, searchPlaceholder: valueSearchPlaceholder, emptyMessage: t('check_exclusions.no_options'), options: accountTagOptions, selected: values, onChange: setValues })),
                scopeLevel === 'building' && (React.createElement(ExclusionMultiSelect, { label: valueFieldLabel, placeholder: valuePlaceholder, searchPlaceholder: valueSearchPlaceholder, emptyMessage: t('check_exclusions.no_building_results'), asyncSearch: asyncBuildingSearch, selected: values, onChange: setValues })),
                scopeLevel === 'building_tag' && (React.createElement(ExclusionMultiSelect, { label: valueFieldLabel, placeholder: valuePlaceholder, searchPlaceholder: valueSearchPlaceholder, emptyMessage: t('check_exclusions.no_options'), options: buildingTagOptions, selected: values, onChange: setValues })))),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { variant: "secondary", onClick: resetAndClose }, t('check_exclusions.cancel')),
            React.createElement(Button, { variant: "primary", disabled: saving || values.length === 0, onClick: handleAdd }, saving ? t('check_exclusions.adding') : t('check_exclusions.add')))));
}
export default AddExclusionModal;
